import { useState } from "react";
import { MapPin, Users, Award, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/components/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ExamRequestForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "", modules: "", location: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.category) { toast.error(t("exams.formError")); return; }
    setLoading(true);
    try {
      await fetch(`${API}/exam-requests`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      toast.success(t("exams.formSuccess"));
      setForm({ name: "", email: "", phone: "", category: "", modules: "", location: "", message: "" });
    } catch { toast.error("Error"); } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-[#0A1628] border border-[#1A2D4A] rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent transition-all text-sm";

  return (
    <form onSubmit={handleSubmit} className="bg-[#0F1D32] border border-[#1A2D4A] rounded-xl p-6" data-testid="exam-request-form">
      <p className="text-white font-bold text-sm mb-1 font-['Outfit']">{t("exams.formTitle")}</p>
      <p className="text-slate-400 text-xs mb-5">{t("exams.formDesc")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("exams.formName")} className={inputCls} data-testid="exam-name" />
        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("exams.formEmail")} className={inputCls} data-testid="exam-email" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("exams.formPhone")} className={inputCls} data-testid="exam-phone" />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${inputCls} ${!form.category ? "text-slate-500" : ""}`} data-testid="exam-category">
          <option value="">{t("exams.formCategory")}</option>
          <option value="A">A</option><option value="B1">B1</option><option value="B2">B2</option><option value="B3">B3</option>
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input type="text" value={form.modules} onChange={(e) => setForm({ ...form, modules: e.target.value })} placeholder={t("exams.formModules")} className={inputCls} data-testid="exam-modules" />
        <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={`${inputCls} ${!form.location ? "text-slate-500" : ""}`} data-testid="exam-location">
          <option value="">{t("exams.formLocation")}</option>
          <option value="Portugal">Portugal</option><option value="Barcelona">Barcelona</option><option value="Madrid">Madrid</option>
        </select>
      </div>
      <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("exams.formMessage")} rows={3} className={`${inputCls} mb-4 resize-vertical`} data-testid="exam-message" />
      <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white font-bold py-3 rounded-lg text-sm shadow-lg shadow-amber-500/20 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50" data-testid="exam-submit-btn">
        {loading ? "..." : t("exams.formSubmit")}<Send className="w-4 h-4" />
      </button>
    </form>
  );
}

export default function ExamesSection() {
  const { t } = useTranslation();
  const moduleNames = t("moduleNames") || [];
  const highlights = [
    { icon: Users, text: t("exams.h1") },
    { icon: MapPin, text: t("exams.h2") },
    { icon: Award, text: t("exams.h3") },
  ];

  return (
    <section id="exames" data-testid="exames-section" className="py-24 md:py-32 bg-[#0A1628]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-2">{t("exams.label")}</p>
            <div className="w-12 h-0.5 bg-gradient-to-r from-[#F59E0B] to-[#00A8FF] mb-6" />
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-extrabold tracking-tight text-white mb-5">{t("exams.title")}</h2>
            <p className="text-base text-slate-400 mb-8 leading-relaxed max-w-lg">{t("exams.desc")}</p>
            <div className="space-y-4 mb-10">
              {highlights.map((h, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center shrink-0">
                    <h.icon className="w-4.5 h-4.5 text-[#F59E0B]" />
                  </div>
                  <p className="text-sm text-slate-300">{h.text}</p>
                </div>
              ))}
            </div>
            <ExamRequestForm />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-4">{t("exams.modulesLabel")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {moduleNames.map((m, i) => (
                <div key={i} className="bg-[#0F1D32] border border-[#1A2D4A] rounded-lg px-4 py-3 flex items-center gap-3 hover:border-[#F59E0B]/30 transition-colors group" data-testid={`exam-module-${i}`}>
                  <span className="text-[#F59E0B] font-bold text-xs font-['Outfit'] w-5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-slate-300 text-sm group-hover:text-white transition-colors">{m}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-2 text-slate-500 text-xs">
              <ArrowRight className="w-3.5 h-3.5" />
              <span>{t("exams.categories")}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
